import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { MenuItem } from 'primeng/api';
import { Invoice } from 'src/app/accounts/invoice/invoice.model';
import { FinancePdfViewButtonComponent } from '../finance-pdf-view-button.component';
import { AccountsService } from 'src/app/accounts/accounts.service';
import { CommonModule, formatDate } from '@angular/common';
import { Receipt } from 'src/app/accounts/receipt/receipt.model';
import { TableLazyLoadEvent, TableModule } from "primeng/table";
import { InvoiceLine } from "./invoice-line.model";
import * as XLSX from 'xlsx';
import { SessionStorageService } from "../../services/session-storage.service";
import { BreadcrumbModule } from 'primeng/breadcrumb';
import { TooltipModule } from 'primeng/tooltip';
import { ButtonModule } from 'primeng/button';
import { ProgressBarModule } from "primeng/progressbar";
import { TagModule } from "primeng/tag";
import { PermissionService } from '../../security/permission.service';
import { app_permissions } from '../../security/app-permissions';
import { finalize } from 'rxjs';
import { PendingActionTracker } from '../../shared/state/pending-action-tracker';

@Component({
	selector: 'app-invoice',
	templateUrl: './invoice.component.html',
	styleUrls: ['./invoice.component.css'],
	standalone: true,
	imports: [FinancePdfViewButtonComponent,
		CommonModule,
		BreadcrumbModule,
		TableModule,
		TooltipModule,
		ButtonModule,
		ProgressBarModule,
		TagModule
	],
	providers: [
		AccountsService,
		SessionStorageService,
		PermissionService
	]
})
export class InvoiceComponent implements OnInit {
	readonly documentActions = new PendingActionTracker<number | undefined>();
	downloadError = '';

	invoices: Invoice[];
	first = 0;
	rows = 20;
	totalRequests = 0;
	isLoading = true;

	breadcrumbItems: MenuItem[];
	homeBreadcrumb: MenuItem;

	canDownloadInvoice: boolean;
	canExportInvoices: boolean;

	constructor(private route: ActivatedRoute, private sessionStorageService: SessionStorageService,
		private router: Router, private accountsService: AccountsService, private permissionService: PermissionService) {
		this.invoices = [];
		this.canDownloadInvoice = this.permissionService.hasPermission(app_permissions.accounts.invoice.view);
		this.canExportInvoices = this.permissionService.hasPermission(app_permissions.accounts.invoice.view);
	}

	ngOnInit(): void {
		this.breadcrumbItems = [{ label: 'Accounts', routerLink: '/accounts' }, { label: 'Invoices' }];
		this.homeBreadcrumb = { icon: 'pi pi-home', routerLink: '/' };
	}

	loadData(event: TableLazyLoadEvent) {
		this.isLoading = true;
		this.first = event.first ?? 0;
		this.rows = event.rows ?? 20;
		let page = event.first ?? 0;
		page = page / 20;
		let pageSize = event.rows ?? 20;
		if (pageSize == 0)
			pageSize = 20;
		this.accountsService.getGeneratedInvoices(page, pageSize, undefined, [this.sessionStorageService.getCompany()!.id]).subscribe(invoicesData => {
			console.log(this.invoices);
			this.invoices = invoicesData.items;
			this.totalRequests = invoicesData.count;
			this.isLoading = false;
		});
	}

	downloadInvoice(invoice: Invoice) {
		if (!invoice.invoiceId || !this.documentActions.begin(invoice.invoiceId)) return;
		this.downloadError = '';
		this.accountsService.downloadInvoiceDocument(invoice.invoiceId)
			.pipe(finalize(() => this.documentActions.end(invoice.invoiceId)))
			.subscribe({ next: pdf => this.accountsService.downloadPdfDocument(pdf, 'invoice', invoice.invoiceNumber, invoice.invoiceId, invoice.postingStatus),
				error: err => this.downloadError = err?.status === 404 ? 'Original unavailable: no stored invoice PDF exists.' : 'The invoice PDF is unavailable.' });
	}

	showDate(longDate: number) {
		return formatDate(new Date(longDate), 'dd-MM-yyyy HH:mm', 'en-US');
	}

	showAllReceiptNumberWithComma(receipts: Receipt[]) {
		return receipts.map(receipt => receipt.receiptNumber).join(', ');
	}

	showInvoiceStatus(invoice: Invoice) {
		const receiptAmount = invoice.receipts.map(receipt => receipt.amount)
			.reduce((subtotal, item) => (subtotal ?? 0) + (item ?? 0), 0);
		console.log(receiptAmount);
		return (receiptAmount ?? 0) >= (invoice.totalAmount ?? 0);
	}

	getUniqueOrderNumbers(invoice: Invoice) {
		return this.removeDuplicates(invoice.invoiceLines.map(invoiceLine => invoiceLine.headerReferenceNumber));
	}

	removeDuplicates<T>(array: T[]): T[] {
		return [...new Set(array)];
	}

	showOrderDetails(invoiceLine: InvoiceLine) {
		window.open(this.router.createUrlTree(
			['/order/view', invoiceLine.headerReferenceNumber]).toString(), '_blank');

	}

	exportToExcel() {
		// Create AOA
		const aoa: any[][] = [];
		aoa.push(['Invoice#', 'Invoice Date', 'Amount Excluding VAT', 'Tax Amount', 'Total Amount', 'Paid With'],
			...this.invoices.map((invoice) => [invoice.invoiceNumber, this.showDate(invoice.invoiceCreationDate!.getTime()),
			invoice.totalAmountExclTax, invoice.totalTax, invoice.totalAmount,
			invoice.receipts.map(receipt => receipt.receiptNumber).join(', ')]))

		const workbook = XLSX.utils.book_new();
		const worksheet = XLSX.utils.aoa_to_sheet(aoa);
		XLSX.utils.book_append_sheet(workbook, worksheet, 'Invoices');

		const fileName = 'cargomate_invoices_export_' + new Date().toISOString() + '.xlsx';
		XLSX.writeFile(workbook, fileName);

		// Create a blob from the workbook
		const blob = new Blob([XLSX.write(workbook,
			{ bookType: 'xlsx', type: 'array' })],
			{
				type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
			});

		// Create a URL for the blob
		const url = URL.createObjectURL(blob);

		const a = document.createElement('a');
		a.href = url;
		a.download = fileName;
		a.target = '_blank';
		a.click();

		// Clean up by revoking the URL
		window.URL.revokeObjectURL(url);
	}
}
